/**
 * EditorialStackBar — a horizontal proportional segmented bar (a single
 * stacked row) with an optional inline legend. The editorial chart
 * treatment: thin hairline frame, accent-ramp fills, mono labels, NO
 * drop-shadow / chart-junk.
 *
 * Editorial reference: `.stack` / `.balance` / `.cumbar` in
 *   docs/design/round3-intent-power.html (Power 4B supply stack),
 *   docs/design/round3-water-climate-env.html (Water 9A balance bar),
 *   docs/design/round3-recs-feasibility-finance.html (Finance cumulative):
 *     a bordered flex strip of width-proportional segments, each a mono
 *     in-segment label on the accent / accent-2 fills.
 *
 * Used by:
 *   - Power Availability (4B)   — Grid award + BTM bridge vs need
 *   - Water Resource (9A)       — Sustainable yield vs peak draw
 *   - Phased Finance (19A/19B)  — cumulative capex strip
 *   - LCOE (D2 cost stack — via the multi-row variant below)
 *
 * `segments` are weighted by `value` (relative magnitude); the component
 * normalises to 100%. Each segment's `color` defaults along the theme
 * accent ramp (accent → accent-soft) so a caller that passes no colours
 * still renders on-theme; explicit `chartPalette`-derived colours can be
 * passed for status semantics. A `needMarker` (e.g. the facility-need
 * line) overlays a vertical rule at a normalised position.
 *
 * Theme tokens read: 'hair' (frame), 'accent' + 'accentSoft' (default
 * fill ramp), 'muted' (legend ink), 'negative' (need marker). Mono
 * labels via --report-body-font. Pure / print-safe.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { BAR_HEIGHT } from '../../../../lib/reportChartLayoutTokens';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface EditorialStackSegment {
  /** Relative magnitude — segments are normalised to 100% of the bar. */
  value: number;
  /** In-bar label (mono, on the fill). Omitted when the segment is too thin. */
  label?: React.ReactNode;
  /** Explicit fill colour; defaults along the theme accent ramp. */
  color?: string;
  /** Render the in-bar label in the ink colour (for pale fills). */
  darkLabel?: boolean;
}

export interface EditorialStackBarProps {
  theme: ReportTheme;
  segments: EditorialStackSegment[];
  /** Bar height in px. Defaults to the tokenized BAR_HEIGHT.stack. */
  height?: number;
  /** Optional legend rows {swatch colour, label}; defaults to segment labels. */
  legend?: Array<{ color: string; label: React.ReactNode }>;
  /** Overlay a vertical need/target marker at this normalised position (0..1). */
  needMarker?: { at: number; label?: React.ReactNode };
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

/** Default fill ramp: accent, accent-2-ish (accent at lower contrast), then
 *  paler accent washes. Derived from theme tokens so it's on-theme per preset. */
function defaultFill(theme: ReportTheme, index: number): string {
  const ramp = [
    roleColor(theme, 'accent'),
    roleColor(theme, 'positive'),
    roleColor(theme, 'muted'),
    roleColor(theme, 'rule'),
  ];
  return ramp[index % ramp.length];
}

export function EditorialStackBar({
  theme,
  segments,
  height = BAR_HEIGHT.stack,
  legend,
  needMarker,
  id,
  className,
  style,
}: EditorialStackBarProps): React.ReactElement | null {
  const usable = segments.filter((s) => Number.isFinite(s.value) && s.value > 0);
  if (usable.length === 0) return null;

  const total = usable.reduce((sum, s) => sum + s.value, 0);
  const hair = roleColor(theme, 'hair');
  const muted = roleColor(theme, 'muted');

  return (
    <div id={id} className={className} style={style}>
      {/* K4 — the strip sits on a thin baseline rule so it reads as a deliberate
           bar block (the reference's supply/balance bars), not a free-floating
           strip. Hairline frame; a faint inter-segment seam (boxShadow) keeps
           adjacent same-hue segments distinct without chart-junk dividers. */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          height,
          border: `1px solid ${hair}`,
          borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
        }}
      >
        {usable.map((seg, i) => {
          const pct = (seg.value / total) * 100;
          const fill = seg.color ?? defaultFill(theme, i);
          return (
            <div
              key={i}
              style={{
                width: `${pct}%`,
                background: fill,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                fontFamily: MONO,
                fontSize: REPORT_TEXT_PX.caption,
                fontWeight: 500,
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                color: seg.darkLabel ? roleColor(theme, 'ink') : '#ffffff',
                // Faint inter-segment seam so neighbouring fills stay distinct.
                boxShadow: i > 0 ? `inset 1px 0 0 ${roleColorAlpha(theme, 'ink', 0.12)}` : undefined,
              }}
            >
              {pct >= 12 ? seg.label : null}
            </div>
          );
        })}
        {needMarker && Number.isFinite(needMarker.at) && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -4,
              bottom: -4,
              left: `${Math.max(0, Math.min(1, needMarker.at)) * 100}%`,
              width: 2,
              background: roleColor(theme, 'negative'),
            }}
          />
        )}
      </div>
      {needMarker?.label != null && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: REPORT_TEXT_PX.micro,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: roleColor(theme, 'negative'),
            marginTop: 5,
            textAlign: 'right',
          }}
        >
          {needMarker.label}
        </div>
      )}
      {legend && legend.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 18,
            marginTop: 12,
            fontFamily: MONO,
            fontSize: REPORT_TEXT_PX.caption,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: muted,
          }}
        >
          {legend.map((row, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                aria-hidden
                style={{ width: 9, height: 9, background: row.color, display: 'inline-block' }}
              />
              {row.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
